import type { CountReturnTypes } from 'api/utils/queries';
import {
  CLAIM_TARIFF_STATUS,
  CLAIM_TARIFF_STATUS_BAND,
  claimTariffStatusEnumValues,
  READY_SUBMISSION_FILTER_ID,
  SUBMITTED_READY_COUNT_KEY,
  type ClaimTariffStatusBandValue,
} from '@billing/validation/tariff';
import { QuickStatusFilterButtons } from 'components/common';
import {
  Box,
  Flex,
  Stack,
  Tabs,
  Text,
  ThemeIcon,
  useComputedColorScheme,
} from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';
import { useTranslation } from '@refinedev/core';
import type { MRT_ColumnFiltersState } from 'mantine-react-table';
import { useEffect, useMemo, type ReactNode } from 'react';
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';
import {
  ALL_CLAIM_TAB,
  DEFAULT_PROVIDER_CLAIM_TAB,
  type ClaimTabKey,
  type ClerkClaimTabKey,
  type ProviderClaimTabKey,
  isClerkClaimTab,
  isProviderClaimTab,
  useClaimTabFilterStore,
} from 'store/ClaimTabFilterStore';
import { providerClaimStatusDefinitions } from 'utils/providerClaimStatus';
import {
  getTariffStatusesForTab,
  getProviderStatusesForBand,
  PROVIDER_SUB_STATUS_DEFINITIONS,
  PROVIDER_STATUS_SELECT_DEFINITIONS,
} from './claimTabFilterConfig';
import {
  providerClaimLegendStyles,
  providerClaimTabStyles as claimBandStyles,
} from '../ProviderClaimsStyles';
import { ProviderClaimStatusLegend } from '../ProviderClaimStatusLegend';

const TARIFF_STATUS_FILTER_ID = 'tariffs.status';
const ACTION_REQUEST_FILTER_ID = 'actionRequest';
const MANAGED_TAB_FILTER_IDS = new Set([
  'status',
  READY_SUBMISSION_FILTER_ID,
  TARIFF_STATUS_FILTER_ID,
  ACTION_REQUEST_FILTER_ID,
  'actionRequest.providerID',
  'actionRequest.userID',
  'inProgress',
  'completed',
]);

interface ClaimTabDefinition<TabKey extends ClaimTabKey> {
  key: TabKey;
  defaultLabel: string;
  band?: ClaimTariffStatusBandValue;
}

const CLERK_TAB_DEFINITIONS: readonly ClaimTabDefinition<ClerkClaimTabKey>[] = [
  { key: 'all', defaultLabel: 'All' },
  { key: 'actionNeeded', defaultLabel: 'Action Needed' },
  { key: 'preSubmission', defaultLabel: 'Pre-submission' },
  { key: 'submittedToPayor', defaultLabel: 'Submitted to Payor' },
  { key: 'underBillerReview', defaultLabel: 'Under Biller Review' },
  { key: 'underPayorReview', defaultLabel: 'Under Payor Review' },
  { key: 'invoiced', defaultLabel: 'Invoiced' },
  { key: 'completed', defaultLabel: 'Completed' },
];

const PROVIDER_TAB_DEFINITIONS: readonly ClaimTabDefinition<ProviderClaimTabKey>[] =
  [
    {
      key: 'preSubmission',
      defaultLabel: 'First submissions',
      band: CLAIM_TARIFF_STATUS_BAND.PRE_SUBMISSION,
    },
    {
      key: 'outstanding',
      defaultLabel: 'Outstanding',
      band: CLAIM_TARIFF_STATUS_BAND.OUTSTANDING,
    },
    {
      key: 'completed',
      defaultLabel: 'Closed',
      band: CLAIM_TARIFF_STATUS_BAND.COMPLETED,
    },
    { key: 'all', defaultLabel: 'All' },
  ];

const areColumnFiltersEqual = (
  currentFilters: MRT_ColumnFiltersState,
  nextFilters: MRT_ColumnFiltersState
): boolean => JSON.stringify(currentFilters) === JSON.stringify(nextFilters);

interface StatusFilterButtonProps {
  statusCounts: CountReturnTypes;
  actionNeededCount?: number;
  isProvider: boolean;
  rightActions?: ReactNode;
}

const sortAlphabetical = (values: string[]) =>
  [...values].sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' })
  );

const getStatusCount = (statusCounts: CountReturnTypes, status: string) => {
  if (!statusCounts || typeof statusCounts === 'number') return 0;
  return Number(statusCounts[status] ?? 0);
};

const getProviderStatusCount = (
  statusCounts: CountReturnTypes,
  status: string,
  band?: ClaimTariffStatusBandValue
): number => {
  const count = getStatusCount(statusCounts, status);
  if (status !== CLAIM_TARIFF_STATUS.READY || !band) return count;
  const submitted = getStatusCount(statusCounts, SUBMITTED_READY_COUNT_KEY);
  if (band === CLAIM_TARIFF_STATUS_BAND.OUTSTANDING) return submitted;
  return count - submitted;
};

const getAllStatusKeys = (statusCounts: CountReturnTypes): string[] => {
  if (!statusCounts || typeof statusCounts === 'number') return [];
  return claimTariffStatusEnumValues.filter((status) => status in statusCounts);
};

const sumTariffStatusCounts = (
  statusCounts: CountReturnTypes,
  statuses: readonly string[],
  band?: ClaimTariffStatusBandValue
): number =>
  [...new Set(statuses)].reduce(
    (total, status) =>
      total + getProviderStatusCount(statusCounts, status, band),
    0
  );

export const StatusFilterButtons = ({
  statusCounts,
  isProvider,
  actionNeededCount = 0,
  rightActions,
}: StatusFilterButtonProps) => {
  const { translate } = useTranslation();
  const colorScheme = useComputedColorScheme('dark');
  const { pagination, setColumnFilters, setPageIndex } =
    useClaimPaginationStore();
  const {
    activeTab,
    providerNeedsAction,
    selectedTariffStatuses,
    setActiveTab,
    setRoleDefaultTab,
    toggleSubStatus,
  } = useClaimTabFilterStore();

  useEffect(() => {
    if (isProvider && !isProviderClaimTab(activeTab)) {
      setRoleDefaultTab(true);
      return;
    }
    if (!isProvider && !isClerkClaimTab(activeTab)) {
      setRoleDefaultTab(false);
    }
  }, [activeTab, isProvider, setRoleDefaultTab]);

  const claimTabs = useMemo(() => {
    const tabDefinitions = isProvider
      ? PROVIDER_TAB_DEFINITIONS
      : CLERK_TAB_DEFINITIONS;
    const translationGroup = isProvider ? 'providerTabFilters' : 'tabFilters';

    return tabDefinitions.map(({ key, defaultLabel, band }) => {
      const tab = {
        key,
        label: translate(
          `pages:claims.${translationGroup}.${key}`,
          defaultLabel
        ),
      };
      if (!isProvider && isClerkClaimTab(key)) {
        if (key === 'actionNeeded') return { ...tab, count: actionNeededCount };
        const statuses =
          key === ALL_CLAIM_TAB
            ? getAllStatusKeys(statusCounts)
            : getTariffStatusesForTab(key);
        return { ...tab, count: sumTariffStatusCounts(statusCounts, statuses) };
      }
      const statuses = band
        ? getProviderStatusesForBand(band)
        : Object.keys(providerClaimStatusDefinitions);
      return {
        ...tab,
        count: sumTariffStatusCounts(statusCounts, statuses, band),
      };
    });
  }, [isProvider, statusCounts, actionNeededCount, translate]);

  const tariffStatusesForTab = useMemo(() => {
    if (!isClerkClaimTab(activeTab)) return [];
    return getTariffStatusesForTab(activeTab);
  }, [activeTab]);

  const tariffStatusFiltersFromUI = useMemo(() => {
    if (isProvider) {
      const explicitStatuses = claimTariffStatusEnumValues.filter((status) =>
        selectedTariffStatuses.includes(status)
      );
      if (explicitStatuses.length > 0) return explicitStatuses;
      const selectedProviderFilter = [
        ...PROVIDER_SUB_STATUS_DEFINITIONS,
        ...PROVIDER_STATUS_SELECT_DEFINITIONS,
      ].find(({ key }) => selectedTariffStatuses.includes(key));
      if (selectedProviderFilter) {
        if (
          selectedProviderFilter.key === 'readyToSubmit' &&
          activeTab === 'outstanding'
        ) {
          return [CLAIM_TARIFF_STATUS.READY];
        }
        return selectedProviderFilter.statuses;
      }

      const tabDefinition = PROVIDER_TAB_DEFINITIONS.find(
        ({ key }) => key === activeTab
      );
      return tabDefinition?.band
        ? getProviderStatusesForBand(tabDefinition.band)
        : [];
    }
    if (selectedTariffStatuses.length > 0) return selectedTariffStatuses;
    if (activeTab === ALL_CLAIM_TAB) return [];
    return tariffStatusesForTab;
  }, [activeTab, isProvider, selectedTariffStatuses, tariffStatusesForTab]);

  useEffect(() => {
    const columnFilters = pagination.columnFilters ?? [];
    const otherFilters = columnFilters.filter(
      (filter) => !MANAGED_TAB_FILTER_IDS.has(filter.id)
    );

    const nextFilters: MRT_ColumnFiltersState = [
      ...otherFilters,
      ...tariffStatusFiltersFromUI.map((status) => ({
        id: TARIFF_STATUS_FILTER_ID,
        value: status,
      })),
    ];

    if (
      isProvider &&
      (activeTab === 'preSubmission' || activeTab === 'outstanding')
    ) {
      nextFilters.push({
        id: READY_SUBMISSION_FILTER_ID,
        value: String(activeTab === 'outstanding'),
      });
    }

    if (
      selectedTariffStatuses.includes(CLAIM_TARIFF_STATUS.READY) &&
      (!isProvider || activeTab === ALL_CLAIM_TAB)
    ) {
      const readySubmissionFilter = columnFilters.find(
        (filter) => filter.id === READY_SUBMISSION_FILTER_ID
      );
      if (readySubmissionFilter) nextFilters.push(readySubmissionFilter);
    }

    if (
      (!isProvider && activeTab === 'actionNeeded') ||
      (isProvider && providerNeedsAction)
    ) {
      nextFilters.push({ id: ACTION_REQUEST_FILTER_ID, value: 'true' });
    }

    if (areColumnFiltersEqual(columnFilters, nextFilters)) return;

    setColumnFilters(nextFilters);
    setPageIndex(0);
  }, [
    activeTab,
    isProvider,
    providerNeedsAction,
    pagination.columnFilters,
    setColumnFilters,
    setPageIndex,
    tariffStatusFiltersFromUI,
    selectedTariffStatuses,
  ]);

  const tariffStatusChipItems = useMemo(() => {
    if (isProvider) {
      const activeProviderTab = activeTab as ProviderClaimTabKey;
      const activeBand = PROVIDER_TAB_DEFINITIONS.find(
        ({ key }) => key === activeProviderTab
      )?.band;
      return PROVIDER_SUB_STATUS_DEFINITIONS.filter(
        ({ band, key }) =>
          activeProviderTab === ALL_CLAIM_TAB ||
          (key === 'readyToSubmit' && activeProviderTab === 'outstanding') ||
          activeBand === band
      ).map((definition) => ({
        key: definition.key,
        label: translate(
          `pages:claims.providerSubStatusFilters.${definition.key}`,
          definition.defaultLabel
        ),
        count: definition.statuses.reduce((sum, status) => {
          if (
            activeProviderTab === 'outstanding' &&
            status === CLAIM_TARIFF_STATUS.REVERTED
          )
            return sum;
          return sum + getProviderStatusCount(statusCounts, status, activeBand);
        }, 0),
        active: selectedTariffStatuses.includes(definition.key),
        dataTestId: `provider-claim-status-filter-${definition.key}`,
      }));
    }
    if (activeTab === 'actionNeeded') return [];
    const statusKeys =
      activeTab === ALL_CLAIM_TAB
        ? sortAlphabetical(getAllStatusKeys(statusCounts))
        : sortAlphabetical(tariffStatusesForTab);
    return statusKeys.map((status) => ({
      key: status,
      label: status,
      count: getStatusCount(statusCounts, status),
      active: selectedTariffStatuses.includes(status),
      dataTestId: `claim-tab-filter-chip-${status
        .replace(/\s+/g, '-')
        .toLowerCase()}`,
    }));
  }, [
    activeTab,
    isProvider,
    statusCounts,
    selectedTariffStatuses,
    tariffStatusesForTab,
    translate,
  ]);

  const providerTabDescription = useMemo(() => {
    if (!isProvider) return null;

    const descriptions: Record<ProviderClaimTabKey, [string, string]> = {
      all: [
        translate(
          'pages:claims.providerTabDescriptions.all.title',
          'All claims.'
        ),
        translate(
          'pages:claims.providerTabDescriptions.all.body',
          'Every provider-visible claim, across each stage of the billing process.'
        ),
      ],
      preSubmission: [
        translate(
          'pages:claims.providerTabDescriptions.preSubmission.title',
          'First submissions.'
        ),
        translate(
          'pages:claims.providerTabDescriptions.preSubmission.body',
          'Draft claims, claims ready for their first submission, and claims submitted to the payor for the first time.'
        ),
      ],
      outstanding: [
        translate(
          'pages:claims.providerTabDescriptions.outstanding.title',
          'Still owed to you.'
        ),
        translate(
          'pages:claims.providerTabDescriptions.outstanding.body',
          'Resubmitted, pending, queried, invoiced, or paid short. Your biller is working these; anything needed from you appears in the claims table.'
        ),
      ],
      completed: [
        translate(
          'pages:claims.providerTabDescriptions.completed.title',
          'Closed claims.'
        ),
        translate(
          'pages:claims.providerTabDescriptions.completed.body',
          'These claims are paid, written off, or otherwise closed.'
        ),
      ],
    };

    return descriptions[activeTab as ProviderClaimTabKey];
  }, [activeTab, isProvider, translate]);

  return (
    <Stack w="100%" gap={0} data-testid="claim-tab-filters">
      <Tabs
        variant="default"
        value={activeTab}
        onChange={(value) => {
          const fallbackTab = isProvider
            ? DEFAULT_PROVIDER_CLAIM_TAB
            : ALL_CLAIM_TAB;
          setActiveTab((value ?? fallbackTab) as ClaimTabKey);
        }}
      >
        <Tabs.List p={0} style={claimBandStyles.getList(colorScheme)}>
          {claimTabs.map((tab) => (
            <Tabs.Tab
              key={tab.key}
              value={tab.key}
              px="lg"
              leftSection={
                !isProvider && tab.key === 'actionNeeded' && tab.count > 0 ? (
                  <IconAlertTriangle
                    size={15}
                    style={claimBandStyles.actionNeededIcon}
                    aria-hidden
                  />
                ) : undefined
              }
              rightSection={
                isProvider && tab.count !== undefined ? (
                  <Box
                    component="span"
                    style={claimBandStyles.getCount(
                      activeTab === tab.key,
                      tab.key === 'actionNeeded' ? tab.count : undefined
                    )}
                  >
                    {tab.count}
                  </Box>
                ) : undefined
              }
              style={claimBandStyles.getTab(activeTab === tab.key)}
            >
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>
      {providerTabDescription ? (
        <Box style={claimBandStyles.description}>
          <Flex align="flex-start" gap="sm">
            <ThemeIcon
              variant="transparent"
              color="blue"
              size="sm"
              style={claimBandStyles.infoIcon}
            >
              <IconInfoCircle size={18} />
            </ThemeIcon>
            <Text size="sm" lh={1.45}>
              <Text component="span" inherit fw={700}>
                {providerTabDescription[0]}
              </Text>{' '}
              <Text component="span" inherit c="dimmed">
                {providerTabDescription[1]}
              </Text>
            </Text>
          </Flex>
        </Box>
      ) : null}
      {activeTab !== ALL_CLAIM_TAB ||
      tariffStatusChipItems.length > 0 ||
      rightActions ? (
        <Flex justify="space-between" align="center" gap="sm">
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Flex
              align="center"
              gap="sm"
              wrap="nowrap"
              style={providerClaimLegendStyles.workflowBar}
            >
              {isProvider ? <ProviderClaimStatusLegend /> : null}
              {tariffStatusChipItems.length > 0 ? (
                <Box style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
                  <QuickStatusFilterButtons
                    items={tariffStatusChipItems}
                    onSelect={toggleSubStatus}
                    topKeys={tariffStatusChipItems.map((item) => item.key)}
                    isLoading={false}
                    enableCollapseOnNarrow={false}
                    muted={isProvider}
                  />
                </Box>
              ) : null}
            </Flex>
          </Box>
          {rightActions ? (
            <Box style={{ flexShrink: 0, marginLeft: 'auto' }}>
              {rightActions}
            </Box>
          ) : null}
        </Flex>
      ) : null}
    </Stack>
  );
};
